import { FC, RefObject, useMemo } from 'react';
import styled from 'styled-components';
import {
  IconButton,
  Popover,
  Stack,
  Typography,
  Box,
  TextField,
} from '@mui/material';
import { Close, FilterDramaOutlined, Search } from '@mui/icons-material';
import {
  chatActions,
  useAppDispatch,
  useGetAssociateSavedRepliesCount,
  useGetAssociateSavedReplies,
} from '@vs-online-only-app/state';
import useDebounce from '../../../../hooks/useDebounce';
import SavedRepliesList from './SavedRepliesList';
import { IllustrationSearchError } from '@vs-online-only-app/ui';

const StyledPopover = styled(Popover)`
  .MuiPaper-root {
    display: flex;
    flex-direction: column;
    width: 400px;
    height: 460px;
    padding: ${({ theme }) => theme.spacing(3)};
  }
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1)};
  right: ${({ theme }) => theme.spacing(1.5)};
`;

type Props = {
  anchorRef: RefObject<HTMLButtonElement>;
};

const MessageComposerSavedRepliesModal: FC<Props> = ({ anchorRef }) => {
  const dispatch = useAppDispatch();
  const {
    value: searchString,
    debouncedValue: debouncedSearchString,
    setValue,
  } = useDebounce<string>('');
  const {
    data: savedReplies,
    isSuccess,
    isLoading,
  } = useGetAssociateSavedReplies({
    type: 'all',
  });
  const { data: allRepliesCount } = useGetAssociateSavedRepliesCount('all');

  const replies = useMemo(() => {
    if (!savedReplies) {
      return [];
    }

    return savedReplies.filter(
      (reply) =>
        reply.content
          .toLowerCase()
          .includes(debouncedSearchString.toLowerCase()) ||
        reply.title.toLowerCase().includes(debouncedSearchString.toLowerCase()),
    );
  }, [debouncedSearchString, savedReplies]);

  const handleClose = () => {
    dispatch(chatActions.setSavedRepliesModalOpen(false));
  };

  return (
    <StyledPopover
      anchorEl={anchorRef?.current}
      open
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
    >
      <CloseButton onClick={handleClose}>
        <Close />
      </CloseButton>
      <Stack gap={1}>
        <Typography fontWeight="bold">
          Saved replies {allRepliesCount ? `(${allRepliesCount})` : ''}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Select a saved reply to add in to your message. You can edit it before
          sending.
        </Typography>
      </Stack>
      <TextField
        onChange={(e) => setValue(e.target.value)}
        value={searchString}
        margin="dense"
        size="small"
        fullWidth
        slotProps={{
          input: { startAdornment: <Search sx={{ marginRight: 1 }} /> },
        }}
        placeholder="Search"
      />
      {isSuccess && debouncedSearchString && replies.length > 0 && (
        <Typography variant="caption" color="textSecondary">
          {replies.length} {replies.length > 1 ? 'results' : 'result'}
        </Typography>
      )}
      {isSuccess && replies.length === 0 && (
        <Stack alignItems="center" height="100%" justifyContent="center">
          {debouncedSearchString ? (
            <IllustrationSearchError />
          ) : (
            <FilterDramaOutlined fontSize="large" />
          )}
          <Typography variant="body2" color="textSecondary">
            {debouncedSearchString
              ? 'No results found'
              : 'No saved replies to show'}
          </Typography>
        </Stack>
      )}
      <SavedRepliesList isLoading={isLoading} savedReplies={replies} />
    </StyledPopover>
  );
};

export default MessageComposerSavedRepliesModal;
